/**
 * Where the sign-in is kept.
 *
 * Browsers keep localStorage per address, so signing in on
 * www.thepolishedversion.com used to leave you signed out on
 * yourcompany.thepolishedversion.com (no owner bar, no admin). On any
 * *.thepolishedversion.com address the session is kept in a cookie for the
 * whole domain instead, so one sign-in (and one sign-out) covers the marketing
 * site, the admin and every business site. Other addresses (custom domains,
 * localhost, previews) keep using localStorage; for custom domains "View
 * Website" carries the sign-in across (src/lib/siteSignIn.ts).
 *
 * Cookies hold about 4 KB, so the value is base64url-encoded and split over
 * numbered cookies: `<key>.0`, `<key>.1`, ...
 */

export const SHARED_AUTH_ROOT_DOMAIN = 'thepolishedversion.com';
const CHUNK_SIZE = 3000;
const MAX_CHUNKS = 20;
const MAX_AGE_SECONDS = 400 * 24 * 60 * 60;
const MIGRATED_FLAG = 'tpv-auth-shared-cookie';

export interface CookieJar {
  cookie: string;
}

export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** ".thepolishedversion.com" for the platform's own addresses, otherwise null. */
export function sharedCookieDomain(hostname: string): string | null {
  const host = hostname.toLowerCase().replace(/\.$/, '');
  if (host === SHARED_AUTH_ROOT_DOMAIN || host.endsWith(`.${SHARED_AUTH_ROOT_DOMAIN}`)) {
    return `.${SHARED_AUTH_ROOT_DOMAIN}`;
  }
  return null;
}

function toBase64Url(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(encoded: string): string | null {
  try {
    const b64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new TextDecoder().decode(bytes);
  } catch {
    return null;
  }
}

function readCookies(jar: CookieJar): Map<string, string> {
  const map = new Map<string, string>();
  for (const part of jar.cookie.split(';')) {
    const eq = part.indexOf('=');
    if (eq < 0) continue;
    map.set(part.slice(0, eq).trim(), part.slice(eq + 1).trim());
  }
  return map;
}

/** A Supabase auth storage that keeps values in domain-wide cookies. */
export function createCookieStorage(jar: CookieJar, domain: string, secure = true): KeyValueStore {
  const attrs = `; Domain=${domain}; Path=/; SameSite=Lax${secure ? '; Secure' : ''}`;
  const write = (name: string, value: string) => {
    jar.cookie = `${name}=${value}; Max-Age=${MAX_AGE_SECONDS}${attrs}`;
  };
  const erase = (name: string) => {
    jar.cookie = `${name}=; Max-Age=0${attrs}`;
  };
  const chunkNames = (key: string, cookies: Map<string, string>) => {
    const names: string[] = [];
    for (let i = 0; i < MAX_CHUNKS && cookies.has(`${key}.${i}`); i++) names.push(`${key}.${i}`);
    return names;
  };

  return {
    getItem(key) {
      const cookies = readCookies(jar);
      const names = chunkNames(key, cookies);
      if (!names.length) return null;
      return fromBase64Url(names.map((n) => cookies.get(n)).join(''));
    },
    setItem(key, value) {
      const encoded = toBase64Url(value);
      const chunks: string[] = [];
      for (let i = 0; i < encoded.length; i += CHUNK_SIZE) chunks.push(encoded.slice(i, i + CHUNK_SIZE));
      if (!chunks.length) chunks.push('');
      const before = chunkNames(key, readCookies(jar)).length;
      chunks.forEach((chunk, i) => write(`${key}.${i}`, chunk));
      for (let i = chunks.length; i < before; i++) erase(`${key}.${i}`);
    },
    removeItem(key) {
      for (const name of chunkNames(key, readCookies(jar))) erase(name);
    },
  };
}

/**
 * Cookie storage that, the first time it runs on an address, moves a sign-in
 * left in that address's localStorage over to the shared cookie (so nobody is
 * signed out by this change) and then stops reading localStorage, so a sign-out
 * on one address can't be undone by an old copy kept on another.
 */
export function createSharedAuthStorage(cookies: KeyValueStore, local: KeyValueStore): KeyValueStore {
  const migrate = (key: string) => {
    let migrated: string | null = null;
    try {
      migrated = local.getItem(`${MIGRATED_FLAG}:${key}`);
    } catch {
      return;
    }
    if (migrated) return;
    try {
      const old = local.getItem(key);
      if (old && cookies.getItem(key) === null) cookies.setItem(key, old);
      local.removeItem(key);
      local.setItem(`${MIGRATED_FLAG}:${key}`, '1');
    } catch {
      // Storage unavailable (private mode etc.): nothing to move.
    }
  };
  return {
    getItem(key) {
      migrate(key);
      return cookies.getItem(key);
    },
    setItem(key, value) {
      migrate(key);
      cookies.setItem(key, value);
    },
    removeItem(key) {
      migrate(key);
      cookies.removeItem(key);
    },
  };
}

/** The storage the Supabase client should use on this page. */
export function pickAuthStorage(): KeyValueStore {
  if (typeof window === 'undefined') return undefined as unknown as KeyValueStore;
  const domain = sharedCookieDomain(window.location.hostname);
  if (!domain || typeof document === 'undefined') return window.localStorage;
  return createSharedAuthStorage(
    createCookieStorage(document, domain, window.location.protocol === 'https:'),
    window.localStorage,
  );
}
